//@ts-nocheck
import PaymentPanel from '../../components/Payment/PaymentPanel/PaymentPanel';
import Box from '@mui/material/Box';
import NotFoundPage from '../../components/NotFoundPage/NotFoundPage';
import { getBookingById } from '../../services/booking/booking';
import { useEffect, useState, useCallback } from 'react';
import { useLoaderData, useLocation } from 'react-router-dom';
import { getStripeConfig } from '../../services/stripe/stripe';
import { getOutputTrip } from '../../services/output/trip';
import {
  getTenantSettings,
  getTripSettings
} from '../../services/tenant/tenant';
import { initGA, trackPageView, trackEvent } from '../../utils/analytics';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  parseTripLogoSetting,
  withResolvedOperatorLogo
} from '../../utils/operatorLogo';
import {
  parseContactSettings,
  withResolvedOperatorContact
} from '../../utils/operatorContact';
import * as PropTypes from 'prop-types';
import './FinalBookingPayment.css';

const SETTINGCODE = 'TEMPLATEFIELDS';
const GLOBAL_CONTACT_SETTINGCODE = 'GLOBALCONTACT';
const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';

/**
 * This is the FinalBookingPayment page component.
 *
 * @namespace Pages
 */

const FinalBookingPayment = ({ id }) => {
  const { bookingId } = useLoaderData();
  const { enableOperatorContactSettings } = useFlags();
  const [isBookingLoading, setIsBookingLoading] = useState(true);
  const [didTripFetchSucceed, setDidTripFetchSucceed] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const bookingKey = queryParams.get('bookingkey');

  const fetchData = useCallback(async () => {
    try {
      const bookingResult = await getBookingById(bookingId, bookingKey);

      if (bookingResult?.data) {
        const [
          promiseConfig,
          promiseTrip,
          promiseTripContact,
          promiseGlobalContact,
          promiseTripLogo
        ] = await Promise.all([
          getStripeConfig(),
          getOutputTrip(bookingResult?.data?.tripid),
          enableOperatorContactSettings
            ? getTripSettings(bookingResult?.data?.tripid, SETTINGCODE).catch(
                () => null
              )
            : Promise.resolve(null),
          enableOperatorContactSettings
            ? getTenantSettings(GLOBAL_CONTACT_SETTINGCODE).catch(() => null)
            : Promise.resolve(null),
          enableOperatorContactSettings
            ? getTripSettings(
                bookingResult?.data?.tripid,
                TRIP_LOGO_SETTINGCODE
              ).catch(() => null)
            : Promise.resolve(null)
        ]);

        const { data: dataConfig } = promiseConfig;
        const { data: originalTripData } = promiseTrip;
        const contactResolvedTripData = withResolvedOperatorContact(
          originalTripData,
          {
            tripContactSettings: parseContactSettings(
              promiseTripContact?.data?.settingvalue
            ),
            globalContactSettings: parseContactSettings(
              promiseGlobalContact?.data?.settingvalue
            ),
            enabled: enableOperatorContactSettings
          }
        );
        const tripData = withResolvedOperatorLogo(contactResolvedTripData, {
          tripLogoUrl: parseTripLogoSetting(
            promiseTripLogo?.data?.settingvalue
          ),
          enabled: enableOperatorContactSettings
        });

        const combinedData = { ...bookingResult?.data, tripData, dataConfig };

        setBookingData(combinedData);

        // Store tenant data in localStorage for currency formatting
        if (tripData?.tenant) {
          const {
            basecurrency,
            sellingcurrency,
            tenantdomain,
            tenantname,
            tenantlogo,
            tenantid
          } = tripData.tenant;

          const tenantData = {
            domain: tenantdomain,
            name: tenantname,
            logo: tenantlogo,
            tenantid: tenantid,
            basecurrency,
            sellingcurrency
          };

          //This serves as a fallback when tenant data is not present in local storage; route middleware is expected to set it
          if (!localStorage.getItem('tenantData')) {
            localStorage.setItem('tenantData', JSON.stringify(tenantData));
          }
        }

        // Update page title
        const tenantName = tripData?.tenant?.tenantname;
        if (tenantName) {
          document.title = `Apply Payment - ${tenantName}`;
        }

        // Initialize GA and track secondary payment page view
        const gaId = tripData?.tenant?.googleanalyticsid;
        if (gaId) {
          const tripId = tripData?.tripid;
          const tripName = tripData?.tripname;
          initGA(gaId);
          trackPageView(`/bookings/${bookingId}/payment`, {
            page_title: tenantName
              ? `Apply Payment - ${tenantName}`
              : undefined,
            booking_id: bookingId,
            trip_id: tripId,
            trip_name: tripName
          });
          trackEvent('secondary_payment_viewed', {
            trip_id: tripId
          });
        }
      } else {
        setDidTripFetchSucceed(false);
      }
    } catch (error) {
      setDidTripFetchSucceed(false);
    } finally {
      setIsBookingLoading(false);
    }
  }, [bookingId, bookingKey, enableOperatorContactSettings]);

  useEffect(() => {
    if (enableOperatorContactSettings === undefined) return;

    fetchData();
  }, [bookingId, bookingKey, enableOperatorContactSettings, fetchData]);

  return (
    <Box
      className='final-booking-payment'
      id={id}
      data-testid='final-booking-payment'
    >
      {!didTripFetchSucceed ? (
        <NotFoundPage id='not-found-page' />
      ) : (
        !isBookingLoading &&
        bookingData && (
          <PaymentPanel
            key={bookingData.bookingid}
            id='payment-panel'
            bookingData={bookingData}
            refreshData={fetchData}
          />
        )
      )}
    </Box>
  );
};

/**
 * Prop type definitions for FinalBookingPayment.
 */
FinalBookingPayment.propTypes = {
  id: PropTypes.string.isRequired
};

export default FinalBookingPayment;
